/**
 * The text-mask rules of `UiInputMaskDirective` as pure functions. In a mask `0` is a digit slot
 * and every other character is a literal (`(000) 000-0000`). The conforming loop is a port of
 * ngx-mask's generic pattern handler (MIT, ngx-mask contributors) reduced to that one token, so
 * fields keep behaving the way they did on ngx-mask.
 */

const DIGIT_SLOT = '0';

export const isDigit = (char: string): boolean => char >= '0' && char <= '9';

export const isMaskLiteral = (char: string, mask: string): boolean =>
  char !== DIGIT_SLOT && mask.includes(char);

export const digitsOf = (text: string): string => text.replace(/\D/g, '');

/** How many digits the mask holds. */
export const maskSlotCount = (mask: string): number =>
  [...mask].filter((char) => char === DIGIT_SLOT).length;

/**
 * The digits of a value that may not fit, cut to the mask from the end: a pasted or autofilled
 * phone usually leads with its country code, and the number itself is what follows.
 */
export const trailingDigits = (text: string, mask: string): string =>
  digitsOf(text).slice(-maskSlotCount(mask) || undefined);

/**
 * Fits any text into the mask: digits fill the slots in order, a literal typed in its own place is
 * kept, a missing literal is filled in once something follows it, and the rest is dropped — as
 * are digits past the last slot. Literals after the last digit stay only when they were typed.
 */
export function conformToMask(raw: string, mask: string): string {
  // A lone character that can't start the mask is rejected rather than turned into a literal.
  if (raw.length === 1 && !isDigit(raw) && raw !== mask[0]) {
    return '';
  }

  let result = '';
  let cursor = 0;
  let index = 0;

  while (index < raw.length && cursor < mask.length) {
    const char = raw[index]!;
    const slot = mask[cursor]!;

    if (slot === DIGIT_SLOT) {
      if (isDigit(char)) {
        result += char;
        cursor++;
      }
      index++;
    } else if (char === slot) {
      result += char;
      cursor++;
      index++;
    } else {
      // The literal is filled in and the same character is tried against the next slot.
      result += slot;
      cursor++;
    }
  }

  const lastSlot = mask[mask.length - 1] ?? DIGIT_SLOT;

  if (result.length > 0 && result.length + 1 === mask.length && lastSlot !== DIGIT_SLOT) {
    result += lastSlot;
  }

  return result;
}

/**
 * Where the caret goes once `count` digits sit before it: right after the last of them, or on the
 * first digit slot when there are none — the leading literals are never typed over.
 */
export function caretAfterDigits(text: string, count: number): number {
  let seen = 0;

  for (let index = 0; index < text.length; index++) {
    if (!isDigit(text[index]!)) continue;
    if (count === 0) return index;

    seen++;

    if (seen === count) return index + 1;
  }

  return text.length;
}

/** The caret stepped left over the literals before it, as a backspace on a literal does. */
export function skipLiteralsBackward(text: string, caret: number): number {
  let position = caret;

  while (position > 0 && !isDigit(text[position - 1]!)) position--;

  return position;
}

/** The caret stepped right over the literals after it, as a forward delete on a literal does. */
export function skipLiteralsForward(text: string, caret: number): number {
  let position = caret;

  while (position < text.length && !isDigit(text[position]!)) position++;

  return position;
}
